/**
 * Team section of Settings (Phase 24): the decisions, kept out of the view.
 */

import type { FirmRole } from '@/types/database';

export interface TeamMember {
  user_id: string;
  email: string;
  role: FirmRole;
  /** False until they've signed in once — an invitation not yet accepted. */
  joined: boolean;
  invited_at: string;
}

/** "3 of 6 seats used" — invitations count, since they hold a seat. */
export function seatSummary(used: number, limit: number): string {
  return `${used} of ${limit} seat${limit === 1 ? '' : 's'} used`;
}

export function seatsLeft(used: number, limit: number): number {
  return Math.max(0, limit - used);
}

export const ROLE_LABEL: Record<FirmRole, string> = {
  owner: 'Owner', admin: 'Admin', member: 'Member',
};

/**
 * What an Admin may do, in one sentence, for the screen that offers the role.
 *
 * Kept next to the rules it describes (0042) rather than written into the
 * template, because it is a promise about permissions and it has to stay true.
 */
export const ADMIN_EXPLAINER =
  'Admins can invite and remove people, change roles and edit your firm’s branding. '
  + 'They can’t remove or demote the owner, change your seat limit, or delete the firm.';

export function memberStatus(m: Pick<TeamMember, 'role' | 'joined'>): string {
  if (!m.joined) {
    // An invitation carries the role it was sent with, and which one it was
    // is the thing worth knowing while it's outstanding.
    return m.role === 'member'
      ? 'Invited — hasn’t signed in yet'
      : `Invited as ${ROLE_LABEL[m.role]} — hasn’t signed in yet`;
  }
  return ROLE_LABEL[m.role];
}

/** Owners and admins manage the firm; members don't (0042). */
export function canManageFirm(role: FirmRole | null | undefined): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * A quick check before sending — the server decides (check_invite, 0034).
 * Returns the problem to show, or null.
 */
export function inviteProblem(email: string, team: Pick<TeamMember, 'email'>[], used: number, limit: number): string | null {
  const e = email.trim().toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return 'Enter an email address.';
  if (team.some((m) => m.email.toLowerCase() === e)) return 'They’re already on your team.';
  if (seatsLeft(used, limit) === 0) return `Your firm has used all ${limit} of its seats.`;
  return null;
}

export interface RowActions {
  canRemove: boolean;
  canResend: boolean;
  /** Offer "Make admin" / "Make member" on this row. */
  canChangeRole: boolean;
  /** The role that button would set, or null when there is no such button. */
  nextRole: Exclude<FirmRole, 'owner'> | null;
}

/**
 * What the viewer may do to one row of the team list. Mirrors the database's
 * rules (0035, 0042) so no button is offered that would only be refused.
 *
 * Three rules, all enforced in the database as well:
 *   - only an owner or admin manages anyone;
 *   - nobody manages themselves — an owner who could demote themselves would
 *     strand the firm, and 0035's deferred check would refuse it anyway;
 *   - nobody manages the OWNER. An admin removing or demoting the owner is
 *     the one thing that would let them take the firm over.
 */
export function rowActions(
  m: Pick<TeamMember, 'user_id' | 'role' | 'joined'>,
  viewer: { userId: string | null; role: FirmRole | null },
): RowActions {
  const manageable = canManageFirm(viewer.role)
    && m.user_id !== viewer.userId
    && m.role !== 'owner';
  return {
    canRemove: manageable,
    canResend: manageable && !m.joined,
    canChangeRole: manageable,
    nextRole: manageable ? (m.role === 'admin' ? 'member' : 'admin') : null,
  };
}

/** The confirmation before changing someone's role. */
export function roleChangeConfirm(
  m: Pick<TeamMember, 'email'>,
  to: Exclude<FirmRole, 'owner'>,
): string {
  return to === 'admin'
    ? `Make ${m.email} an admin?\n\n${ADMIN_EXPLAINER}`
    : `Make ${m.email} a member?\n\nThey’ll keep access to the firm’s projects, `
      + 'but will no longer be able to invite or remove people, change roles, or edit branding.';
}

/** The confirmation shown before removing — different for a pending invite. */
export function removeConfirmText(m: Pick<TeamMember, 'email' | 'joined'>): string {
  return m.joined
    ? `Remove ${m.email} from your firm?\n\nThey won’t be able to see the firm’s projects any more. `
      + 'Everything they worked on stays with the firm.'
    : `Cancel the invitation to ${m.email}?\n\nThe link in their email will stop working, and their seat is freed.`;
}
